import { motion } from "framer-motion";

/** SVG-based dashboard mockups per product — treated as spotlit editorial artwork. */

const ChromeBar = ({ label }) => (
    <div className="browser-chrome">
        <div className="browser-dot bg-red-400/70" />
        <div className="browser-dot bg-yellow-400/70" />
        <div className="browser-dot bg-green-400/70" />
        <div className="ml-3 text-white/50 text-[10px] font-mono tracking-wider">{label}</div>
    </div>
);

export function EStampMockup() {
    return (
        <div className="browser-frame">
            <ChromeBar label="app.colecle.com/estamp" />
            <div className="p-5 grid grid-cols-5 gap-4 bg-[#0F1E3A]">
                <div className="col-span-2 space-y-3">
                    <div className="text-white/50 text-[10px] font-mono tracking-widest">DOCUMENT AUTHENTICATION</div>
                    <div className="text-white text-lg font-serif">Ministry Contract 8/2026</div>
                    <div className="space-y-2 mt-4">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="bg-white/[0.04] rounded-md px-3 py-2 border border-white/5">
                                <div className="h-2 bg-white/10 rounded w-3/4 mb-1.5" />
                                <div className="h-1.5 bg-white/5 rounded w-1/2" />
                            </div>
                        ))}
                    </div>
                </div>
                <div className="col-span-3 relative">
                    <div className="bg-gradient-to-br from-[#2563EB]/20 to-transparent rounded-lg border border-white/10 p-6 h-full flex flex-col items-center justify-center gap-3">
                        <svg viewBox="0 0 100 100" className="w-20 h-20">
                            <circle cx="50" cy="50" r="46" fill="none" stroke="#E8B923" strokeWidth="2" strokeDasharray="4 4" />
                            <circle cx="50" cy="50" r="34" fill="none" stroke="#2563EB" strokeWidth="1.5" />
                            <text x="50" y="45" textAnchor="middle" fill="#E8B923" fontSize="9" fontFamily="Fraunces" fontWeight="600">COLECLE</text>
                            <text x="50" y="58" textAnchor="middle" fill="#fff" fontSize="6" fontFamily="Inter" opacity="0.6">VERIFIED</text>
                            <text x="50" y="66" textAnchor="middle" fill="#fff" fontSize="4.5" fontFamily="JetBrains Mono" opacity="0.4">#00 4821</text>
                        </svg>
                        <div className="text-[#E8B923] text-[10px] font-mono tracking-widest">CRYPTOGRAPHIC SEAL</div>
                        <div className="text-white/60 text-[10px] font-mono">SHA-256 · 2026-09-23</div>
                        <div className="mt-2 grid grid-cols-6 gap-0.5 w-28">
                            {Array.from({ length: 36 }).map((_, i) => (
                                <div key={i} className="aspect-square" style={{ background: Math.random() > 0.45 ? "#E8B923" : "#0B1930" }} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function MonitorMockup() {
    const bars = [40, 65, 30, 80, 55, 90, 45, 70, 35, 60, 50, 85];
    return (
        <div className="browser-frame">
            <ChromeBar label="monitor.colecle.com" />
            <div className="p-5 bg-[#0F1E3A]">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="text-white/50 text-[10px] font-mono tracking-widest">LIVE TELEMETRY</div>
                        <div className="text-white text-lg font-serif mt-1">Infrastructure health</div>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] font-mono">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                        <span className="text-green-400">99.98% uptime</span>
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                    {["CPU", "MEM", "NET"].map((k, i) => (
                        <div key={k} className="bg-white/[0.04] rounded-md p-3 border border-white/5">
                            <div className="text-white/50 text-[9px] font-mono">{k}</div>
                            <div className="text-white text-lg font-serif mt-0.5">{[42, 68, 24][i]}%</div>
                            <div className="mt-2 h-1 bg-white/10 rounded overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-[#10B981] to-[#2563EB]" style={{ width: `${[42, 68, 24][i]}%` }} />
                            </div>
                        </div>
                    ))}
                </div>
                <div className="bg-white/[0.03] rounded-md p-3 border border-white/5">
                    <div className="flex items-end gap-1.5 h-20">
                        {bars.map((h, i) => (
                            <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-[#10B981] to-[#2563EB]" style={{ height: `${h}%` }} />
                        ))}
                    </div>
                    <div className="flex items-center justify-between mt-2 text-[9px] font-mono text-white/40">
                        <span>-12h</span><span>now</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function ProcurementMockup() {
    const stages = ["Plan", "Tender", "Evaluate", "Award", "Contract", "Deliver"];
    return (
        <div className="browser-frame">
            <ChromeBar label="app.colecle.com/procurement" />
            <div className="p-5 bg-[#0F1E3A]">
                <div className="text-white/50 text-[10px] font-mono tracking-widest">PROCUREMENT PIPELINE</div>
                <div className="text-white text-lg font-serif mt-1 mb-4">Active tenders — Q3</div>
                <div className="grid grid-cols-6 gap-2 mb-5">
                    {stages.map((s, i) => (
                        <div key={s} className="text-center">
                            <div className={`h-1 rounded-full ${i <= 3 ? "bg-[#E8B923]" : "bg-white/10"}`} />
                            <div className={`mt-2 text-[9px] font-mono ${i <= 3 ? "text-[#E8B923]" : "text-white/40"}`}>{s}</div>
                            <div className="text-white text-sm mt-1 font-serif">{[24, 18, 12, 8, 6, 4][i]}</div>
                        </div>
                    ))}
                </div>
                <div className="space-y-2">
                    {[
                        { name: "IT Infrastructure Refresh", value: "$2.4M", status: "Evaluation", color: "#E8B923" },
                        { name: "Fleet Management Services", value: "$860K", status: "Award", color: "#10B981" },
                        { name: "Building Renovation Phase 2", value: "$1.1M", status: "Tender", color: "#2563EB" },
                    ].map((r) => (
                        <div key={r.name} className="flex items-center justify-between bg-white/[0.04] rounded px-3 py-2 border border-white/5">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: r.color }} />
                                <div className="text-white text-xs truncate">{r.name}</div>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                                <div className="text-white/50 text-[10px] font-mono">{r.status}</div>
                                <div className="text-[#E8B923] text-xs font-mono">{r.value}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export function RentMockup() {
    return (
        <div className="browser-frame">
            <ChromeBar label="rent.colecle.com" />
            <div className="p-5 bg-[#0F1E3A]">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="text-white/50 text-[10px] font-mono tracking-widest">PORTFOLIO REVENUE</div>
                        <div className="text-white text-lg font-serif mt-1">$248,320 collected</div>
                    </div>
                    <div className="text-[10px] text-green-400 font-mono">+12.4% MoM</div>
                </div>
                <svg viewBox="0 0 300 80" className="w-full h-20 mb-4">
                    <defs>
                        <linearGradient id="g1" x1="0" x2="0" y1="0" y2="1">
                            <stop offset="0" stopColor="#8B5CF6" stopOpacity="0.5" />
                            <stop offset="1" stopColor="#8B5CF6" stopOpacity="0" />
                        </linearGradient>
                    </defs>
                    <path d="M0,60 C40,40 60,55 90,35 S150,20 180,30 220,15 260,20 290,10 300,15 L300,80 L0,80 Z" fill="url(#g1)" />
                    <path d="M0,60 C40,40 60,55 90,35 S150,20 180,30 220,15 260,20 290,10 300,15" fill="none" stroke="#E8B923" strokeWidth="1.5" />
                </svg>
                <div className="grid grid-cols-4 gap-2">
                    {[
                        { l: "Units", v: "142" },
                        { l: "Occupied", v: "128" },
                        { l: "Arrears", v: "$4.2K" },
                        { l: "Renewals", v: "18" },
                    ].map((x) => (
                        <div key={x.l} className="bg-white/[0.04] rounded p-2.5 border border-white/5">
                            <div className="text-white/50 text-[9px] font-mono">{x.l}</div>
                            <div className="text-white text-sm font-serif mt-0.5">{x.v}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export function EventsMockup() {
    return (
        <div className="browser-frame">
            <ChromeBar label="events.colecle.com" />
            <div className="p-5 bg-[#0F1E3A]">
                <div className="text-white/50 text-[10px] font-mono tracking-widest">LIVE CHECK-IN</div>
                <div className="text-white text-lg font-serif mt-1 mb-4">Enterprise Summit '26</div>
                <div className="flex gap-4 items-center mb-4">
                    <div className="relative w-20 h-20">
                        <svg viewBox="0 0 80 80" className="w-20 h-20 -rotate-90">
                            <circle cx="40" cy="40" r="34" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="6" />
                            <circle cx="40" cy="40" r="34" fill="none" stroke="#EC4899" strokeWidth="6" strokeDasharray="213" strokeDashoffset="42" strokeLinecap="round" />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <div className="text-white text-lg font-serif">80%</div>
                            <div className="text-white/50 text-[8px] font-mono">CHECKED-IN</div>
                        </div>
                    </div>
                    <div className="flex-1 space-y-2">
                        {[
                            { l: "Registered", v: "412" },
                            { l: "Checked in", v: "329" },
                            { l: "VIPs", v: "24" },
                        ].map((x) => (
                            <div key={x.l} className="flex items-center justify-between text-xs">
                                <span className="text-white/60 font-mono text-[10px]">{x.l}</span>
                                <span className="text-white font-serif">{x.v}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="grid grid-cols-8 gap-1">
                    {Array.from({ length: 32 }).map((_, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0.2 }}
                            animate={{ opacity: i < 26 ? 1 : 0.2 }}
                            transition={{ delay: i * 0.02 }}
                            className="aspect-square rounded-sm"
                            style={{ background: i < 26 ? "#EC4899" : "rgba(255,255,255,0.06)" }}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

export const MOCKUPS = {
    estamp: EStampMockup,
    monitor: MonitorMockup,
    procurement: ProcurementMockup,
    rent: RentMockup,
    events: EventsMockup,
};
